export type PushNotificationState =
  | "unsupported"
  | "default"
  | "denied"
  | "loading"
  | "subscribing"
  | "subscribed"
  | "testing"
  | "error";

export type PushSubscriptionJSON = {
  endpoint: string;
  expirationTime?: number | null;
  keys: { auth: string; p256dh: string };
};

export type PushSettingsResponse = {
  vapidPublicKey: string;
  subscribed: boolean;
};

export type PushApi = {
  settings: () => Promise<PushSettingsResponse>;
  saveSubscription: (subscription: PushSubscriptionJSON) => Promise<void>;
  deleteSubscription: () => Promise<void>;
  test: () => Promise<void>;
};

export type PushBrowser = {
  supported: boolean;
  permission: NotificationPermission;
  register: () => Promise<ServiceWorkerRegistration>;
  requestPermission: () => Promise<NotificationPermission>;
};

export type PushSnapshot = {
  state: PushNotificationState;
  message?: string;
};

type PushOperationCoordinator = {
  run: (pendingState: PushNotificationState, operation: () => Promise<PushSnapshot>) => Promise<PushSnapshot | undefined>;
  stopAndDrain: () => Promise<void>;
  resume: () => void;
};

type PushOwnerStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const PUSH_OWNER_KEY = "meeting-radar-push-owner";

export function browserPush(): PushBrowser {
  const supported =
    typeof window !== "undefined" &&
    window.isSecureContext &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  return {
    supported,
    permission: supported ? Notification.permission : "default",
    register: () => navigator.serviceWorker.register("/service-worker.js", { scope: "/" }),
    requestPermission: () => Notification.requestPermission()
  };
}

export async function registerPushServiceWorker(browser = browserPush()): Promise<ServiceWorkerRegistration | null> {
  if (!browser.supported) return null;
  return browser.register();
}

export async function inspectPushNotifications(
  api: PushApi,
  ownerKey: string,
  browser = browserPush(),
  storage: PushOwnerStorage | undefined = globalThis.localStorage
): Promise<PushSnapshot> {
  if (!browser.supported) return { state: "unsupported" };
  if (browser.permission === "denied") return { state: "denied" };
  if (browser.permission === "default") return { state: "default" };

  try {
    const [registration, settings] = await Promise.all([browser.register(), api.settings()]);
    const browserSubscription = await registration.pushManager.getSubscription();
    const existing = await subscriptionForOwner(browserSubscription, settings.subscribed, ownerKey, storage);
    if (!browserSubscription && !settings.subscribed) {
      clearPushOwner(storage);
      return { state: "default" };
    }
    const subscription = await reconcileBrowserSubscription(registration, settings.vapidPublicKey, existing);
    await api.saveSubscription(subscription.toJSON() as PushSubscriptionJSON);
    writePushOwner(ownerKey, storage);
    return { state: "subscribed" };
  } catch {
    return { state: "error", message: "Notification settings could not be loaded." };
  }
}

export async function enablePushNotifications(
  api: PushApi,
  ownerKey: string,
  browser = browserPush(),
  storage: PushOwnerStorage | undefined = globalThis.localStorage
): Promise<PushSnapshot> {
  if (!browser.supported) return { state: "unsupported" };

  try {
    const permission = browser.permission === "granted"
      ? "granted"
      : await browser.requestPermission();
    if (permission === "denied") return { state: "denied" };
    if (permission !== "granted") return { state: "default" };

    const settings = await api.settings();
    if (!settings.vapidPublicKey) throw new Error("missing VAPID public key");
    const registration = await browser.register();
    const browserSubscription = await registration.pushManager.getSubscription();
    const existing = await subscriptionForOwner(browserSubscription, settings.subscribed, ownerKey, storage);
    const subscription = await reconcileBrowserSubscription(registration, settings.vapidPublicKey, existing);
    await api.saveSubscription(subscription.toJSON() as PushSubscriptionJSON);
    writePushOwner(ownerKey, storage);
    return { state: "subscribed" };
  } catch {
    return { state: "error", message: "Notifications could not be enabled. Try again." };
  }
}

export async function disablePushNotifications(
  api: PushApi,
  browser = browserPush(),
  storage: PushOwnerStorage | undefined = globalThis.localStorage
): Promise<PushSnapshot> {
  const cleaned = await cleanupPushNotifications(api, browser, storage);
  if (cleaned) {
    return { state: browser.permission === "denied" ? "denied" : "default" };
  }
  return { state: "error", message: "Notifications could not be disabled. Try again." };
}

async function cleanupPushNotifications(
  api: PushApi,
  browser = browserPush(),
  storage: PushOwnerStorage | undefined = globalThis.localStorage
): Promise<boolean> {
  const deleteFromServer = Promise.resolve().then(() => api.deleteSubscription());
  const unsubscribeBrowser = browser.supported
    ? Promise.resolve().then(() => browser.register()).then(async (registration) => {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
      })
    : Promise.resolve();
  const results = await Promise.allSettled([deleteFromServer, unsubscribeBrowser]);
  if (results[1]?.status === "fulfilled") clearPushOwner(storage);
  return results.every((result) => result.status === "fulfilled");
}

export async function signOutWithPushCleanup(
  api: PushApi,
  signOut: () => Promise<void>,
  browser = browserPush(),
  storage: PushOwnerStorage | undefined = globalThis.localStorage,
  stopAndDrain: () => Promise<void> = async () => {}
): Promise<void> {
  await stopAndDrain();
  await cleanupPushNotifications(api, browser, storage);
  await signOut();
}

export async function testPushNotifications(api: PushApi): Promise<PushSnapshot> {
  try {
    await api.test();
    return { state: "subscribed", message: "Test notification sent." };
  } catch {
    return { state: "error", message: "The test notification could not be sent." };
  }
}

export function decodeVapidPublicKey(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const decoded = atob(base64);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

export function createPushOperationCoordinator(apply: (snapshot: PushSnapshot) => void): PushOperationCoordinator {
  let latestOperation = 0;
  let accepting = true;
  let tail = Promise.resolve();
  const active = new Set<Promise<PushSnapshot>>();
  return {
    run(pendingState, operation) {
      if (!accepting) return Promise.resolve(undefined);
      const operationId = ++latestOperation;
      apply({ state: pendingState });
      const execution = tail.catch(() => undefined).then(operation);
      tail = execution.then(() => undefined, () => undefined);
      active.add(execution);
      return execution.then((snapshot) => {
        if (accepting && operationId === latestOperation) apply(snapshot);
        return snapshot;
      }).finally(() => active.delete(execution));
    },
    async stopAndDrain() {
      accepting = false;
      latestOperation += 1;
      await Promise.allSettled([...active]);
    },
    resume() {
      accepting = true;
    }
  };
}

export function pushOwnerKey(userId: string, organizationId: string): string {
  return JSON.stringify([organizationId, userId]);
}

async function subscriptionForOwner(
  existing: PushSubscription | null,
  serverProvesCurrentOwnership: boolean,
  ownerKey: string,
  storage: PushOwnerStorage | undefined
): Promise<PushSubscription | null> {
  if (!existing) return null;
  if (readPushOwner(storage) === ownerKey || serverProvesCurrentOwnership) return existing;
  await existing.unsubscribe();
  clearPushOwner(storage);
  return null;
}

function readPushOwner(storage: PushOwnerStorage | undefined): string | null {
  try { return storage?.getItem(PUSH_OWNER_KEY) ?? null; } catch { return null; }
}

function writePushOwner(ownerKey: string, storage: PushOwnerStorage | undefined): void {
  try { storage?.setItem(PUSH_OWNER_KEY, ownerKey); } catch { /* restricted storage */ }
}

function clearPushOwner(storage: PushOwnerStorage | undefined): void {
  try { storage?.removeItem(PUSH_OWNER_KEY); } catch { /* restricted storage */ }
}

async function reconcileBrowserSubscription(
  registration: ServiceWorkerRegistration,
  vapidPublicKey: string,
  existing: PushSubscription | null
): Promise<PushSubscription> {
  if (!vapidPublicKey) throw new Error("missing VAPID public key");
  const applicationServerKey = decodeVapidPublicKey(vapidPublicKey);
  if (existing && applicationServerKeysEqual(existing.options.applicationServerKey, applicationServerKey)) {
    return existing;
  }
  if (existing) await existing.unsubscribe();
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
}

function applicationServerKeysEqual(actual: BufferSource | null, expected: Uint8Array<ArrayBuffer>): boolean {
  if (!actual) return false;
  const bytes = actual instanceof ArrayBuffer
    ? new Uint8Array(actual)
    : new Uint8Array(actual.buffer, actual.byteOffset, actual.byteLength);
  return bytes.length === expected.length && bytes.every((value, index) => value === expected[index]);
}

let inMemoryDeviceId: string | undefined;

export function pushDeviceId(storage: Pick<Storage, "getItem" | "setItem"> | undefined = globalThis.localStorage): string {
  const key = "meeting-radar-push-device-id";
  try {
    const stored = storage?.getItem(key);
    if (stored) return stored;
  } catch {
    // A stable in-memory ID still permits the current session in restricted storage modes.
  }
  inMemoryDeviceId ??= cryptographicDeviceId();
  try { storage?.setItem(key, inMemoryDeviceId); } catch { /* restricted storage */ }
  return inMemoryDeviceId;
}

function cryptographicDeviceId(): string {
  const crypto = globalThis.crypto;
  if (!crypto) throw new Error("Web Crypto is required to create a push device ID");
  if (crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
